import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { surfaceAt, type Level } from "../level";
import type { MapLook } from "../maps";
import type { Skater } from "../skater";
import { blobTexture } from "./textures";
import type { EnvironmentKind } from "./styles/types";
import { toon } from "./toon";

// Sky, clouds and the skater's soft contact shadow.

/**
 * A gradient sky dome: deeper blue overhead fading to a pale haze at the
 * horizon that matches the fog, with a warm glow toward the sun.
 */
export function buildSky(look: MapLook, sunDir: THREE.Vector3): THREE.Mesh {
  const horizon = new THREE.Color(look.sky);
  const zenith = look.skyTop ? new THREE.Color(look.skyTop) : horizon.clone().offsetHSL(0.02, 0.15, -0.22);
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      zenith: { value: zenith },
      horizon: { value: horizon },
      glow: { value: new THREE.Color("#fff3d6") },
      sunDir: { value: sunDir.clone().normalize() },
    },
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 zenith;
      uniform vec3 horizon;
      uniform vec3 glow;
      uniform vec3 sunDir;
      varying vec3 vDir;
      void main() {
        float t = smoothstep(0.0, 0.55, max(vDir.y, 0.0));
        vec3 col = mix(horizon, zenith, t);
        float sun = pow(max(dot(vDir, sunDir), 0.0), 6.0);
        col = mix(col, glow, sun * 0.45);
        gl_FragColor = vec4(col, 1.0);
        #include <colorspace_fragment>
      }`,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });
  const dome = new THREE.Mesh(new THREE.SphereGeometry(320, 32, 16), mat);
  dome.renderOrder = -1;
  dome.frustumCulled = false;
  return dome;
}

/**
 * The scene's environment map (reflections and ambient bounce), prefiltered
 * for PBR. `studio` is three's soft RoomEnvironment; `sky` renders the map's
 * sky dome with a bright sun in it, so the bounce matches the sky (#75,
 * realistic). Indoors there's no sky to see, so it's the studio either way.
 */
export function buildEnvironment(renderer: THREE.WebGLRenderer, kind: EnvironmentKind, look: MapLook, sunDir: THREE.Vector3): THREE.Texture | null {
  const pmrem = new THREE.PMREMGenerator(renderer);
  let tex: THREE.Texture | null;
  if (kind === "studio" || look.indoor) {
    tex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  } else {
    const scene = new THREE.Scene();
    const dome = buildSky(look, sunDir);
    const sunGeo = new THREE.SphereGeometry(6, 16, 8);
    const sunMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1, 0.94, 0.82).multiplyScalar(40), fog: false });
    const sun = new THREE.Mesh(sunGeo, sunMat);
    sun.position.copy(sunDir).normalize().multiplyScalar(150);
    // A warm ground bounce under the horizon.
    const groundGeo = new THREE.CircleGeometry(300, 24);
    const groundMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(look.hemiGround).multiplyScalar(0.6), side: THREE.DoubleSide, fog: false });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -2;
    scene.add(dome, sun, ground);
    tex = pmrem.fromScene(scene, 0.02, 0.1, 1000).texture;
    for (const m of [dome, sun, ground]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  }
  pmrem.dispose();
  return tex;
}

/** Puffy clouds: clusters of soft spheres, flattened on the bottom. */
export function buildClouds(): THREE.Group {
  const clouds = new THREE.Group();
  const mat = toon("#ffffff", { roughness: 1, emissive: "#dfe9f5", emissiveIntensity: 0.35 }, "plastic");
  const puffGeo = new THREE.SphereGeometry(1, 16, 12);
  for (let i = 0; i < 14; i++) {
    const cloud = new THREE.Group();
    for (let j = 0; j < 5; j++) {
      const puff = new THREE.Mesh(puffGeo, mat);
      const s = 3 + Math.random() * 3 * (j === 2 ? 1.4 : 1);
      puff.scale.set(s * 1.2, s * 0.8, s);
      puff.position.set(j * 3.5 - 7, Math.random() * 1.5 + (j === 2 ? 1.5 : 0), Math.random() * 3);
      cloud.add(puff);
    }
    const a = (i / 14) * Math.PI * 2;
    const r = 110 + Math.random() * 60;
    cloud.position.set(Math.cos(a) * r, 40 + Math.random() * 25, Math.sin(a) * r);
    cloud.lookAt(0, cloud.position.y, 0);
    clouds.add(cloud);
  }
  return clouds;
}

/**
 * A soft dark blob on the ground under the skater: grounds them when rolling
 * (the shadow map alone is thin at the wheels) and shows where you'll land,
 * fading and spreading with height.
 */
export class ContactBlob {
  readonly mesh: THREE.Mesh;
  private mat: THREE.MeshBasicMaterial;
  private up = new THREE.Vector3(0, 1, 0);
  private n = new THREE.Vector3();
  private q = new THREE.Quaternion();
  private yawQ = new THREE.Quaternion();

  constructor() {
    const tex = blobTexture();
    this.mat = new THREE.MeshBasicMaterial({
      color: 0x1a1426,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      ...(tex ? { alphaMap: tex } : {}),
    });
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.rotateX(-Math.PI / 2);
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.renderOrder = 2;
    this.mesh.visible = !!tex;
  }

  update(level: Level, sk: Skater): void {
    if (!this.mat.alphaMap) return;
    const s = surfaceAt(level, sk.x, sk.z);
    const height = Math.max(0, sk.y - s.h);
    this.mesh.position.set(sk.x, s.h + 0.01, sk.z);
    this.n.set(-s.gx, 1, -s.gz).normalize();
    // Tilt onto the ground, then turn along the board (its nose is local +z).
    this.q.setFromUnitVectors(this.up, this.n);
    this.yawQ.setFromAxisAngle(this.up, sk.yaw);
    this.mesh.quaternion.copy(this.q).multiply(this.yawQ);
    const spread = 1 + height * 0.25;
    this.mesh.scale.set(0.75 * spread, 1, 1.1 * spread);
    this.mat.opacity = 0.5 / (1 + height * 0.6);
  }
}
